import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  VStack,
  Input,
  Badge,
  InputGroup,
  InputLeftElement,
  Image,
  IconButton,
  Text,
  Icon,
  useBreakpointValue,
  SimpleGrid,
  useColorModeValue,
  Progress,
  Avatar,
  Spinner,
  useToast,
  Divider,
  Tag,
  Link,
  Menu,
  MenuButton,
  Button,
  MenuList,
  MenuItem,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Container,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  
  CircularProgress,
  CircularProgressLabel
} from '@chakra-ui/react';
import {
  SearchIcon,
  BellIcon,
  StarIcon,
  CalendarIcon,
  ChevronDownIcon,
  ExternalLinkIcon,
  ViewIcon,
  TimeIcon
} from '@chakra-ui/icons';
import { 
  FiBookOpen, 
  FiVideo, 
  FiCalendar, 
  FiTrendingUp,
  FiAward,
  FiClock,
  FiPlay,
  FiBookmark,
  FiTarget,
  FiActivity,
  FiUsers,
  FiStar
} from 'react-icons/fi';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
} from 'chart.js';
import { motion } from 'framer-motion';
import Sidebar from '../shared/Sidebar';
import { SidebarProvider, useSidebar } from '../utils/SidebarContext';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';
import { format, isToday, isTomorrow, addDays } from 'date-fns';
import { ar } from 'date-fns/locale';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const DashboardContent = () => {
  const { isOpen } = useSidebar();
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const contentMarginRight = useBreakpointValue({ base: '0px', md: isOpen ? '250px' : '100px' });
  
  // Color scheme based on home page palette
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('#2C3E50', 'white');

  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    enrolledCourses: 0,
    completedVideos: 0,
    upcomingEvents: 0,
    overallProgress: 0,
    recentCourses: [],
    upcomingDeadlines: [],
    weeklyActivity: [],
    achievements: [],
    studyStreak: 0,
    totalStudyTime: 0
  });

  const [chartData, setChartData] = useState({
    weeklyProgress: {
      labels: ['السبت', 'الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة'],
      datasets: [
        {
          label: 'ساعات الدراسة',
          data: [2, 3, 1, 4, 2, 5, 3],
          borderColor: primaryColor,
          backgroundColor: `${primaryColor}20`,
          tension: 0.4,
          fill: true,
        }
      ],
    },
    courseProgress: {
      labels: ['مكتمل', 'قيد التقدم', 'لم يبدأ'],
      datasets: [
        {
          data: [30, 50, 20],
          backgroundColor: [
            '#10b981',
            primaryColor,
            '#e5e7eb'
          ],
          borderWidth: 0,
        },
      ],
    }
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch user's enrolled courses
      const coursesResponse = await axiosInstance.get('/dashboard/courses/enrolled');
      const eventsResponse = await axiosInstance.get('/dashboard/events/upcoming');
      const progressResponse = await axiosInstance.get('/dashboard/user/progress');
      
      // Mock data for demonstration - replace with actual API calls
      const mockData = {
        enrolledCourses: coursesResponse.data?.length || 5,
        completedVideos: 24,
        upcomingEvents: eventsResponse.data?.length || 3,
        overallProgress: 68,
        recentCourses: [
          {
            id: 1,
            title: 'الرياضيات المتقدمة',
            progress: 75,
            lastAccessed: new Date(),
            instructor: 'د. أحمد محمد',
            thumbnail: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb'
          },
          {
            id: 2,
            title: 'الفيزياء النووية',
            progress: 45,
            lastAccessed: new Date(Date.now() - 86400000),
            instructor: 'د. فاطمة علي',
            thumbnail: 'https://images.unsplash.com/photo-1636466497217-26a8cbeaf0aa'
          },
          {
            id: 3,
            title: 'الكيمياء العضوية',
            progress: 90,
            lastAccessed: new Date(Date.now() - 172800000),
            instructor: 'د. محمد حسن',
            thumbnail: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d'
          }
        ],
        upcomingDeadlines: [
          {
            id: 1,
            title: 'امتحان الرياضيات',
            date: addDays(new Date(), 2),
            type: 'exam',
            course: 'الرياضيات المتقدمة'
          },
          {
            id: 2,
            title: 'تسليم مشروع الفيزياء',
            date: addDays(new Date(), 5),
            type: 'assignment',
            course: 'الفيزياء النووية'
          },
          {
            id: 3,
            title: 'محاضرة مباشرة',
            date: addDays(new Date(), 1),
            type: 'lecture',
            course: 'الكيمياء العضوية'
          }
        ],
        achievements: [
          { id: 1, title: 'أول دورة مكتملة', icon: FiAward, color: 'gold' },
          { id: 2, title: 'أسبوع متواصل', icon: FiTarget, color: 'blue' },
          { id: 3, title: 'متفوق في الرياضيات', icon: FiStar, color: 'purple' }
        ],
        studyStreak: 7,
        totalStudyTime: 45
      };

      setDashboardData(mockData);

    } catch (error) {
      console.error('خطأ في جلب بيانات لوحة التحكم:', error);
      toast({
        title: 'خطأ في تحميل البيانات',
        description: 'فشل في تحميل بيانات لوحة التحكم',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            family: 'Cairo, sans-serif',
            size: 12
          },
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(44, 62, 80, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: primaryColor,
        borderWidth: 1,
        cornerRadius: 8,
        titleFont: {
          family: 'Cairo, sans-serif'
        },
        bodyFont: {
          family: 'Cairo, sans-serif'
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            family: 'Cairo, sans-serif'
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          font: {
            family: 'Cairo, sans-serif'
          }
        }
      },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            family: 'Cairo, sans-serif',
            size: 12
          },
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(44, 62, 80, 0.9)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: primaryColor,
        borderWidth: 1,
        cornerRadius: 8,
      },
    },
  };

  const getDeadlineColor = (date) => {
    if (isToday(date)) return 'red';
    if (isTomorrow(date)) return 'orange';
    return 'blue';
  };

  const getDeadlineText = (date) => {
    if (isToday(date)) return 'اليوم';
    if (isTomorrow(date)) return 'غداً';
    return format(date, 'dd/MM', { locale: ar });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'exam': return FiTarget;
      case 'assignment': return FiBookOpen;
      case 'lecture': return FiVideo;
      default: return FiCalendar;
    }
  };

  if (isLoading) {
    return (
      <Flex h="100vh" align="center" justify="center" dir="rtl">
        <VStack spacing={4}>
          <Spinner size="xl" color={primaryColor} thickness="4px" />
          <Text color={textColor} fontFamily="Cairo, sans-serif">جاري تحميل لوحة التحكم...</Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box dir="rtl" fontFamily="Cairo, sans-serif">
      <Flex h="100vh" overflowX="hidden" flexDirection="column">
        <Sidebar />

        <Box
          flex="1"
          mr={contentMarginRight}
          p={6}
          bg={bgColor}
          transition="margin-right 0.3s ease"
          overflowY="auto"
        >
          {/* Header */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={6}>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={4}
                >
                  <VStack align="start" spacing={2}>
                    <Heading 
                      as="h1" 
                      size="xl" 
                      color={secondaryColor} 
                      fontFamily="Cairo, sans-serif"
                      fontWeight="700"
                    >
                      مرحباً، {user?.username || 'الطالب'}! 👋
                    </Heading>
                    <Text color="gray.600" fontSize="md">
                      {format(new Date(), 'EEEE، dd MMMM yyyy', { locale: ar })}
                    </Text>
                  </VStack>
                  
                  <HStack spacing={4} flexDirection={{ base: 'column', md: 'row' }}>
                    <InputGroup maxW="300px">
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.400" />
                      </InputLeftElement>
                      <Input 
                        placeholder="البحث في الدورات..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        bg="gray.50"
                        border="2px solid"
                        borderColor="gray.200"
                        _focus={{
                          borderColor: primaryColor,
                          boxShadow: `0 0 0 1px ${primaryColor}`
                        }}
                        borderRadius="lg"
                      />
                    </InputGroup>
                    
                    <Menu>
                      <MenuButton as={Button} rightIcon={<ChevronDownIcon />} bg="white" border="2px solid" borderColor="gray.200">
                        <Avatar size="sm" name={user?.username} bg={primaryColor} color={secondaryColor} />
                      </MenuButton>
                      <MenuList>
                        <MenuItem icon={<ViewIcon />}>عرض الملف الشخصي</MenuItem>
                        <MenuItem icon={<BellIcon />}>الإشعارات</MenuItem>
                      </MenuList>
                    </Menu>
                  </HStack>
                </Flex>
              </CardBody>
            </Card>
          </motion.div>

          {/* Quick Stats */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            الدورات المسجلة
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {dashboardData.enrolledCourses}
                          </StatNumber>
                          <StatHelpText color="green.500" fontSize="sm">
                            <StatArrow type="increase" />
                            نشطة
                          </StatHelpText>
                        </Box>
                        <Box 
                          bg={`${primaryColor}20`} 
                          p={3} 
                          borderRadius="lg"
                        >
                          <Icon as={FiBookOpen} color={primaryColor} boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            الفيديوهات المكتملة
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {dashboardData.completedVideos}
                          </StatNumber>
                          <StatHelpText color="blue.500" fontSize="sm">
                            هذا الشهر
                          </StatHelpText>
                        </Box>
                        <Box 
                          bg="blue.100" 
                          p={3} 
                          borderRadius="lg"
                        >
                          <Icon as={FiVideo} color="blue.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            سلسلة الدراسة
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {dashboardData.studyStreak}
                          </StatNumber>
                          <StatHelpText color="orange.500" fontSize="sm">
                            أيام متتالية
                          </StatHelpText>
                        </Box>
                        <Box 
                          bg="orange.100" 
                          p={3} 
                          borderRadius="lg"
                        >
                          <Icon as={FiTarget} color="orange.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            إجمالي ساعات الدراسة
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {dashboardData.totalStudyTime}
                          </StatNumber>
                          <StatHelpText color="purple.500" fontSize="sm">
                            ساعة هذا الشهر
                          </StatHelpText>
                        </Box>
                        <Box 
                          bg="purple.100" 
                          p={3} 
                          borderRadius="lg"
                        >
                          <Icon as={FiClock} color="purple.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>
            </SimpleGrid>
          </motion.div>

          {/* Progress Overview */}
          <motion.div variants={fadeIn}>
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={8}>
              <CardHeader>
                <Heading size="md" color={secondaryColor} fontFamily="Cairo, sans-serif">
                  نظرة عامة على التقدم
                </Heading>
              </CardHeader>
              <CardBody>
                <Flex align="center" justify="center" mb={6}>
                  <CircularProgress 
                    value={dashboardData.overallProgress} 
                    size="120px" 
                    color={primaryColor}
                    thickness="8px"
                  >
                    <CircularProgressLabel fontSize="xl" fontWeight="bold" color={secondaryColor}>
                      {dashboardData.overallProgress}%
                    </CircularProgressLabel>
                  </CircularProgress>
                </Flex>
                <Text textAlign="center" color="gray.600" fontFamily="Cairo, sans-serif">
                  التقدم الإجمالي في جميع الدورات
                </Text>
              </CardBody>
            </Card>
          </motion.div>

          {/* Charts and Recent Activity */}
          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} mb={8}>
            {/* Weekly Activity Chart */}
            <motion.div variants={fadeIn}>
              <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                <CardHeader>
                  <Heading size="md" color={secondaryColor} fontFamily="Cairo, sans-serif">
                    النشاط الأسبوعي
                  </Heading>
                </CardHeader>
                <CardBody>
                  <Box h="300px">
                    <Line data={chartData.weeklyProgress} options={chartOptions} />
                  </Box>
                </CardBody>
              </Card>
            </motion.div>

            {/* Course Progress Distribution */}
            <motion.div variants={fadeIn}>
              <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                <CardHeader>
                  <Heading size="md" color={secondaryColor} fontFamily="Cairo, sans-serif">
                    توزيع تقدم الدورات
                  </Heading>
                </CardHeader>
                <CardBody>
                  <Box h="300px">
                    <Doughnut data={chartData.courseProgress} options={pieOptions} />
                  </Box>
                </CardBody>
              </Card>
            </motion.div>
          </SimpleGrid>

          {/* Recent Courses and Upcoming Deadlines */}
          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} mb={8}>
            {/* Recent Courses */}
            <motion.div variants={fadeIn}>
              <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                <CardHeader>
                  <Flex align="center" justify="space-between">
                    <Heading size="md" color={secondaryColor} fontFamily="Cairo, sans-serif">
                      الدورات الأخيرة
                    </Heading>
                    <Icon as={FiBookOpen} color={primaryColor} boxSize={5} />
                  </Flex>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    {dashboardData.recentCourses.map((course) => (
                      <Box key={course.id} p={4} bg="gray.50" borderRadius="lg" _hover={{ bg: 'gray.100' }} cursor="pointer">
                        <Flex align="center" gap={4}>
                          <Image 
                            src={course.thumbnail} 
                            alt={course.title}
                            boxSize="60px"
                            borderRadius="lg"
                            objectFit="cover"
                          />
                          <Box flex="1">
                            <Text fontWeight="600" color={textColor} fontFamily="Cairo, sans-serif" mb={1}>
                              {course.title}
                            </Text>
                            <Text fontSize="sm" color="gray.500" fontFamily="Cairo, sans-serif" mb={2}>
                              {course.instructor}
                            </Text>
                            <Progress 
                              value={course.progress} 
                              colorScheme="yellow" 
                              size="sm" 
                              borderRadius="full"
                            />
                            <Text fontSize="xs" color="gray.500" mt={1} fontFamily="Cairo, sans-serif">
                              {course.progress}% مكتمل
                            </Text>
                          </Box>
                          <IconButton
                            icon={<FiPlay />}
                            aria-label="متابعة الدورة"
                            size="sm"
                            colorScheme="yellow"
                            variant="ghost"
                          />
                        </Flex>
                      </Box>
                    ))}
                  </VStack>
                </CardBody>
              </Card>
            </motion.div>

            {/* Upcoming Deadlines */}
            <motion.div variants={fadeIn}>
              <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                <CardHeader>
                  <Flex align="center" justify="space-between">
                    <Heading size="md" color={secondaryColor} fontFamily="Cairo, sans-serif">
                      المواعيد النهائية القادمة
                    </Heading>
                    <Icon as={FiCalendar} color={primaryColor} boxSize={5} />
                  </Flex>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    {dashboardData.upcomingDeadlines.map((deadline) => (
                      <Box key={deadline.id} p={4} bg="gray.50" borderRadius="lg">
                        <Flex align="center" justify="space-between">
                          <HStack spacing={3}>
                            <Box 
                              bg={`${getDeadlineColor(deadline.date)}.100`} 
                              p={2} 
                              borderRadius="lg"
                            >
                              <Icon 
                                as={getTypeIcon(deadline.type)} 
                                color={`${getDeadlineColor(deadline.date)}.500`} 
                                boxSize={4} 
                              />
                            </Box>
                            <Box>
                              <Text fontWeight="600" color={textColor} fontFamily="Cairo, sans-serif">
                                {deadline.title}
                              </Text>
                              <Text fontSize="sm" color="gray.500" fontFamily="Cairo, sans-serif">
                                {deadline.course}
                              </Text>
                            </Box>
                          </HStack>
                          <Badge 
                            colorScheme={getDeadlineColor(deadline.date)} 
                            variant="subtle"
                            borderRadius="full"
                            px={3}
                            py={1}
                            fontFamily="Cairo, sans-serif"
                          >
                            {getDeadlineText(deadline.date)}
                          </Badge>
                        </Flex>
                      </Box>
                    ))}
                  </VStack>
                </CardBody>
              </Card>
            </motion.div>
          </SimpleGrid>

          {/* Achievements */}
          <motion.div variants={fadeIn}>
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
              <CardHeader>
                <Flex align="center" justify="space-between">
                  <Heading size="md" color={secondaryColor} fontFamily="Cairo, sans-serif">
                    الإنجازات الأخيرة
                  </Heading>
                  <Icon as={FiAward} color={primaryColor} boxSize={5} />
                </Flex>
              </CardHeader>
              <CardBody>
                <HStack spacing={4} overflowX="auto" pb={2}>
                  {dashboardData.achievements.map((achievement) => (
                    <Tooltip key={achievement.id} label={achievement.title} placement="top">
                      <Box
                        minW="80px"
                        h="80px"
                        bg={`${achievement.color}.100`}
                        borderRadius="lg"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        _hover={{ transform: 'scale(1.05)' }}
                        transition="all 0.2s"
                      >
                        <Icon 
                          as={achievement.icon} 
                          color={`${achievement.color}.500`} 
                          boxSize={8} 
                        />
                      </Box>
                    </Tooltip>
                  ))}
                </HStack>
              </CardBody>
            </Card>
          </motion.div>
        </Box>
      </Flex>
    </Box>
  );
};

const Dashboard = () => (
  <SidebarProvider>
    <DashboardContent />
  </SidebarProvider>
);

export default Dashboard;